import React, { useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { useAuth } from '@opendoor/auth-fe/context';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import StaticSiteHeader from 'components/static-site/StaticSiteHeader';
import { useRouter } from 'next/router';

type ContentOffset = 'standard' | 'compactDesktop';

interface AuthenticatedDashboardLayoutProps {
  children: ReactNode;
  contentOffset: ContentOffset;
}

const contentOffsetClassNames: Record<ContentOffset, string> = {
  standard: 'pt-31',
  compactDesktop: 'pt-31 md:pt-18',
};

const AuthenticatedDashboardLayout: React.FC<AuthenticatedDashboardLayoutProps> = ({
  children,
  contentOffset,
}) => {
  const { authentication } = useAuth();
  const router = useRouter();

  const shouldRedirect =
    authentication.state === 'not_authenticated' || authentication.state === 'error';

  useEffect(() => {
    if (shouldRedirect) {
      window.location.assign(`/login?redirectTo=${encodeURIComponent(router.asPath)}`);
    }
  }, [router.asPath, shouldRedirect]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <Col className="overflow-x-hidden bg-od-bg-primary">
      <Box className="fixed inset-x-0 top-0 z-[2] block w-full shadow-md">
        <StaticSiteHeader slug="dashboard" />
      </Box>
      <Col
        as="main"
        className={`min-h-screen w-full bg-od-bg-primary ${contentOffsetClassNames[contentOffset]}`}
      >
        {children}
      </Col>
    </Col>
  );
};

const BuyerDashboardLayout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: createMarketplaceQueryCache(),
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            gcTime: 0,
          },
        },
      }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <AuthenticatedDashboardLayout contentOffset="standard">
        {children}
      </AuthenticatedDashboardLayout>
    </QueryClientProvider>
  );
};

export const getBuyerDashboardLayout = (page: ReactElement) => (
  <BuyerDashboardLayout>{page}</BuyerDashboardLayout>
);

export const getHomeResaleDashboardLayout = (page: ReactElement) => (
  <AuthenticatedDashboardLayout contentOffset="compactDesktop">{page}</AuthenticatedDashboardLayout>
);
